import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Languages, ArrowRight, Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, authFetch } from "@/App";
import { refreshFabricLibrary, responseError } from "./useFabricLibrary";

// Admin table: a collection name seen in emails (e.g. German site) -> the collection used in the library.
export function FabricCollectionAliases({ library }) {
  const [form, setForm] = useState({ alias: "", collection: "" });
  const [saving, setSaving] = useState(false);
  const collections = useMemo(() => [...new Set(library.list.map((s) => s.collection).filter(Boolean))].sort(), [library.list]);

  const add = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/fabric-aliases`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!res.ok) { toast.error(await responseError(res)); return; }
      await refreshFabricLibrary();
      toast.success("Équivalence enregistrée");
      setForm({ alias: "", collection: "" });
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer l'équivalence « ${a.alias} » = « ${a.collection} » ?`)) return;
    const res = await authFetch(`${API}/fabric-aliases/${a.alias_id}`, { method: "DELETE" });
    if (!res.ok) { toast.error(await responseError(res)); return; }
    await refreshFabricLibrary();
    toast.success("Équivalence supprimée");
  };

  return (
    <div className="p-3 mb-5 rounded-lg border border-zinc-200" data-testid="fabric-aliases-section">
      <div className="flex items-center gap-2 mb-1">
        <Languages className="w-4 h-4 text-orange-500" />
        <h3 className="text-sm font-semibold text-zinc-900">Équivalences de collections <span className="font-normal text-zinc-400" data-testid="fabric-aliases-count">({library.aliases.length})</span></h3>
      </div>
      <p className="text-xs text-zinc-500 mb-3">Même toile, autre nom selon le pays du site (ex. « S&P EXKLUSIV 2025 » sur le site allemand = « S&P SELECTION 2025 »). Les commandes utiliseront les photos de la collection de la bibliothèque.</p>
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px]"><label className="text-xs font-medium text-zinc-600">Nom dans l'email</label><Input value={form.alias} onChange={(e) => setForm((f) => ({ ...f, alias: e.target.value }))} placeholder="S&P EXKLUSIV 2025" className="h-9" data-testid="fabric-alias-input" /></div>
        <ArrowRight className="w-4 h-4 text-zinc-400 mb-2.5" />
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-medium text-zinc-600">Collection de la bibliothèque</label>
          <Input value={form.collection} onChange={(e) => setForm((f) => ({ ...f, collection: e.target.value }))} list="fabric-alias-collections" placeholder="S&P SELECTION 2025" className="h-9" data-testid="fabric-alias-collection-input" />
          <datalist id="fabric-alias-collections">{collections.map((c) => <option key={c} value={c} />)}</datalist>
        </div>
        <Button onClick={add} disabled={saving || !form.alias.trim() || !form.collection.trim()} className="h-9 gap-1.5 bg-orange-500 hover:bg-orange-600" data-testid="fabric-alias-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Ajouter
        </Button>
      </div>
      {library.aliases.length > 0 && (
        <ul className="mt-3 divide-y divide-zinc-100 border border-zinc-100 rounded-md" data-testid="fabric-aliases-list">
          {library.aliases.map((a, i) => (
            <li key={a.alias_id} className="flex items-center gap-2 px-3 py-1.5 text-sm" data-testid={`fabric-alias-row-${i}`}>
              <span className="font-medium text-zinc-800">{a.alias}</span><ArrowRight className="w-3.5 h-3.5 text-zinc-400" /><span className="text-zinc-600 flex-1">{a.collection}</span>
              <button type="button" onClick={() => remove(a)} className="p-1 rounded hover:bg-red-50 transition-colors" title="Supprimer" data-testid={`fabric-alias-delete-${i}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
